import { cssTransition } from 'react-toastify';
import { createGlobalStyle, keyframes } from 'styled-components';
import { theme, toRem } from 'design';

// react-toastify ships its animations in ReactToastify.css, which we deliberately don't import —
// it also carries default toast chrome (padding, radius, shadow, sans-serif) we'd only override back.
// The library just toggles a class and sets animation-duration inline, so every keyframe below is
// ours. Without them nothing animates, and the exit animation is what react-toastify listens to in
// order to delete the toast.
const trackProgress = keyframes({
  '0%': {
    transform: 'scaleX(1)',
  },
  '100%': {
    transform: 'scaleX(0)',
  },
});

// Enters by sliding down from one toast-height above its resting spot while fading in; leaves by
// fading in place. Mirrors the framer-motion animation this replaced:
//   initial={{ opacity: 0, top: '-100%' }} animate={{ opacity: 1, top: 'auto' }} exit={{ opacity: 0 }}
// translateY is used instead of `top` — same visual, but compositable and immune to the layout
// rules being reworked around it.
const slideDownIn = keyframes({
  from: {
    opacity: 0,
    transform: 'translateY(-100%)',
  },
  to: {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

const fadeOut = keyframes({
  from: {
    opacity: 1,
  },
  to: {
    opacity: 0,
  },
});

// react-toastify only toggles these class names and sets animation-duration inline, so the
// keyframes have to be ours — the names here must stay in sync with the rules below.
// collapse: false matches AnimatePresence, which removed the node as soon as the fade finished;
// flip it to true if you'd rather the remaining toasts slide up instead of jumping.
export const ToastTransition = cssTransition({
  enter: 'kf-toast-enter',
  exit: 'kf-toast-exit',
  duration: 300,
  collapse: false,
});

const ToastStyles = createGlobalStyle`
  /* Wrapper only. It holds no layout role — the container below positions itself against the
     viewport — so this collapses to zero height and stays click-through. */
  .Toastify {
    pointer-events: none;
  }

  /* Centred with left/translateX rather than left: 0; right: 0 so the box still shrink-wraps the
     toast on desktop. Stretching it edge to edge would widen the pointer-events: all region into a
     full-width band that swallows clicks either side of the toast. */
  .Toastify__toast-container {
    position: fixed;
    top: ${toRem(theme.space.xl)};
    left: 50%;
    z-index: ${theme.zIndex.toast};
    transform: translateX(-50%);
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 100%;
    padding: 0 ${toRem(theme.space.xl)};
    pointer-events: all;

    @media (min-width: ${theme.breakpoint.small}px) {
      width: auto;
    }
  }

  .Toastify__toast {
    justify-content: center;
    width: 100%;
    cursor: pointer;

    @media (min-width: ${theme.breakpoint.small}px) {
      width: auto;
    }

    &.Toastify__toast--default {
      width: 100%;
      min-height: ${toRem(77)};
      padding: 0;
      margin-bottom: ${toRem(theme.space.md)};
      border-radius: ${toRem(5.5)};
      background-color: ${theme.color.white};
      box-shadow: ${theme.shadow.dialog};
      overflow: visible;

      @media (min-width: ${theme.breakpoint.small}px) {
        width: auto;
        min-width: ${toRem(385)};
        max-width: ${toRem(450)};
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  // The progress bar is hidden (hideProgressBar), but its animationend is what closes the toast,
  // so it still has to run for autoClose to work at all.
  .Toastify__progress-bar {
    animation: ${trackProgress} linear 1;
  }

  .kf-toast-enter {
    animation-name: ${slideDownIn};
  }

  .kf-toast-exit {
    animation-name: ${fadeOut};
  }

  .Toastify__toast-body {
    width: 100%;
    margin: 0;
    flex: 1 1 auto;

    @media (min-width: ${theme.breakpoint.small}px) {
      width: auto;
    }
  }
`;

export default ToastStyles;
